import React from 'react';

// Helper function to normalize consecutive new lines to double new lines
export const normalizeNewLines = (text: string): string => {
  return text.replace(/\n{2,}/g, '\n\n');
};

// Helper function to remove asterisks
export const removeAsterisks = (text: string): string => {
  return text.replace(/\*/g, '');
};

// Helper function to normalize curly quotes to straight quotes
export const normalizeQuotes = (text: string): string => {
  return text.replace(/[\u201C\u201D]/g, '"');
};

// Enhanced segment-based parsing function for robust message formatting
export const parseMessageSegments = (content: string): React.ReactNode => {
  const normalizedContent = normalizeNewLines(normalizeQuotes(removeAsterisks(content)));
  
  const parts: Array<{ content: string; isDialogue: boolean; originalIndex: number }> = [];
  let lastIndex = 0;
  
  const quoteRegex = /"[^"]*?"/g;
  let match;
  
  while ((match = quoteRegex.exec(normalizedContent)) !== null) {
    if (match.index > lastIndex) {
      const narrativeContent = normalizedContent.slice(lastIndex, match.index);
      if (narrativeContent) {
        parts.push({
          content: narrativeContent,
          isDialogue: false,
          originalIndex: lastIndex
        });
      }
    }
    
    const dialogueContent = match[0];
    parts.push({
      content: dialogueContent,
      isDialogue: true,
      originalIndex: match.index
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < normalizedContent.length) {
    const remainingContent = normalizedContent.slice(lastIndex);
    if (remainingContent) {
      parts.push({
        content: remainingContent,
        isDialogue: false,
        originalIndex: lastIndex
      });
    }
  }
  
  if (parts.length === 0) {
    parts.push({
      content: normalizedContent,
      isDialogue: false,
      originalIndex: 0
    });
  }
  
  const formattedElements: React.ReactNode[] = [];
  
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    
      const partElement = part.isDialogue ? (
    <span key={`part-${part.originalIndex}`} style={{ color: '#ededed' }}>{part.content}</span>
  ) : (
    <span key={`part-${part.originalIndex}`} style={{ color: '#b0b0b0', fontStyle: 'italic' }}>{part.content}</span>
  );
    
    formattedElements.push(partElement);
  }
  
  return (
    <React.Fragment>
      {formattedElements}
    </React.Fragment>
  );
};

// Format dialogue versus narrative with quote-based parsing
export const formatMessageContent = (
  content: string, 
  isStreaming?: boolean,
  userPreferredName?: string,
  characterName?: string
) => {
  let processedContent = content.trim();  
  // Replace placeholders with actual names
  if (userPreferredName) {
    processedContent = processedContent.replace(/\{\{user\}\}/g, userPreferredName);
  }
  if (characterName) {
    processedContent = processedContent.replace(/\{\{char\}\}/g, characterName);
  }
  
  return parseMessageSegments(processedContent);
}; 

// Parse hints from backend response
export const parseHints = (content: string): string[] => {
  const hints: string[] = [];
  
  // Convert to lowercase for case-insensitive matching and split by hint separators
  const lowerContent = content.toLowerCase();
  const originalContent = content;
  
  // Find hint 1
  const hint1Index = lowerContent.indexOf('response 1:');
  const hint2Index = lowerContent.indexOf('response 2:');
  const hint3Index = lowerContent.indexOf('response 3:');
  
  if (hint1Index !== -1) {
    const startIndex = hint1Index + 'response 1:'.length;
    const endIndex = hint2Index !== -1 ? hint2Index : hint3Index !== -1 ? hint3Index : content.length;
    const hint1Content = originalContent.substring(startIndex, endIndex).trim();
    if (hint1Content) {
      hints.push(hint1Content);
    }
  }
  
  if (hint2Index !== -1) {
    const startIndex = hint2Index + 'response 2:'.length;
    const endIndex = hint3Index !== -1 ? hint3Index : content.length;
    const hint2Content = originalContent.substring(startIndex, endIndex).trim();
    if (hint2Content) {
      hints.push(hint2Content);
    }
  }
  
  if (hint3Index !== -1) {
    const startIndex = hint3Index + 'response 3:'.length;
    const hint3Content = originalContent.substring(startIndex).trim();
    if (hint3Content) {
      hints.push(hint3Content);
    }
  }
  
  return hints;
};

// Extract dialogue sections from content (text inside quotes), excluding quotes
export const parseDialogues = (content: string): string[] => {
  const dialogues: string[] = [];

  // Regex to find all quoted text
  const quoteRegex = /"([^"]*?)"/g;
  let match;

  // Extract each quoted section (excluding the quotes)
  while ((match = quoteRegex.exec(content)) !== null) {
    const dialogueContent = match[1]; // Content inside quotes
    if (dialogueContent.trim()) {
      dialogues.push(dialogueContent);
    }
  }

  return dialogues;
}; 